/**
 * DraftsDrawer — lists open drafts across all recipes.
 *
 * Entry point: the "N drafts" chip in LibraryView (Wave 2). This drawer
 * is the Wave 5 wiring of that chip.
 *
 * Design refs:
 *   design.md §2 — hairlines over shadows; 200 ms ease; warm neutrals only.
 *   design.md §4 — token usage: --text, --muted, --line, --font-mono.
 *   design.md §6 — Library route context.
 *   branding.md §5.2.1 — no banned words; no exclamations; no emoji in strings.
 *   Sheet README — controlled API: snapPoints=["peek","full"], no dismissGuard.
 *
 * Architecture refs:
 *   attempt.list filtered by status="draft" per variation (branch).
 *   N+1 approach is intentional at MVP — recipe counts are small and there is
 *   no cross-variation draft-list endpoint. A future `draft.listAll` procedure
 *   can replace this without changing the consumer API.
 *
 * CLAUDE.md Rule 7 — mobile + tablet + desktop from first commit.
 * CLAUDE.md Rule 8 — fetch failures surface as inline error copy; no bare catch.
 */

import { useState, useEffect, useCallback } from "react";
import { Sheet, SheetHeader, SheetBody } from "@proof/ui";
import { isLegacyMultiVariation } from "../workspace/variationNaming.js";

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * Minimal recipe shape required by DraftsDrawer.
 *
 * Compatible with both:
 *   - `Recipe` from `~/features/explorer/types` (the runtime state type in app.tsx)
 *   - `RecipeListRow` from `@proof/client-core/api/recipes`
 *
 * Using a local minimal type avoids coupling the drawer to a specific import
 * path and lets the props accept either shape without casting.
 */
export interface DraftsDrawerRecipe {
  uuid: string;
  title: string;
  /** User-chosen emoji — optional; drawer renders a placeholder when absent. */
  emoji?: string;
  /** Variations on this recipe. Each must have a UUID and display name. */
  variations?: Array<{ uuid: string; name: string }>;
}

/**
 * A single open draft across all recipes and variations.
 *
 * Internal type — not exported. The DraftsDrawer owns this shape; callers
 * pass `recipes` + `apiClient` and receive navigation callbacks.
 */
interface DraftRow {
  /** UUID of the attempt (draft). */
  attemptUuid: string;
  /** UUID of the variation (branch) that owns this draft. */
  variationUuid: string;
  /** UUID of the parent recipe. */
  recipeUuid: string;
  /** Display name of the recipe. */
  recipeTitle: string;
  /** Emoji on the recipe (or null when absent). */
  recipeEmoji: string | null;
  /** Display name of the variation. */
  variationName: string;
  /**
   * How many variations the parent recipe carries. The variation name is shown
   * on the row ONLY for legacy multi-variation recipes (versions-first-header,
   * 2026-07-09) — a single-variation recipe (the norm) carries no "— main"
   * git-ism.
   */
  recipeVariationCount: number;
  /** Last-updated epoch-ms timestamp from the attempt row. */
  updatedAt: number;
}

/** Wire shape from attempt.list — only the fields we need. */
interface AttemptWire {
  uuid: string;
  branch_uuid: string;
  status: string;
  created_at: number;
  updated_at?: number;
}

/** Wire shape from attempt.list response. */
interface AttemptListResponse {
  attempts: AttemptWire[];
}

export interface DraftsDrawerProps {
  /** Whether the drawer is open. */
  open: boolean;
  /** Called when the drawer closes (× button, scrim tap, drag-to-dismiss). */
  onClose: () => void;
  /**
   * Recipe list from the Library's existing data. Each row must include
   * `variations[]` metadata (uuid + name per variation) to drive the N+1
   * draft fetch. Compatible with `Recipe` from `~/features/explorer/types`
   * and `RecipeListRow` from `@proof/client-core/api/recipes`.
   */
  recipes: DraftsDrawerRecipe[];
  /**
   * Typed oRPC API client (typed as `any` following the established pattern
   * in apps/web — see app.tsx WorkspaceContainer).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  apiClient: any;
  /**
   * Called when the user taps a draft row. The parent activates the recipe +
   * sets the doc key to navigate to the draft editor.
   *
   * @param recipeUuid - UUID of the recipe to activate.
   * @param variationUuid - UUID of the variation whose draft to open.
   */
  onNavigateToDraft: (recipeUuid: string, variationUuid: string) => void;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const listStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  padding: 0,
  margin: 0,
};

function draftRowStyle(interactive: boolean): React.CSSProperties {
  return {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "var(--space-3, 12px)",
    minHeight: "48px",
    padding: "var(--space-2, 8px) 0",
    cursor: interactive ? "pointer" : "default",
    borderBottom: "1px solid var(--line, #dcd7cc)",
    background: "none",
    border: "none",
    width: "100%",
    textAlign: "left",
    WebkitTapHighlightColor: "transparent",
    transition: "background 200ms ease",
  };
}

const emojiCellStyle: React.CSSProperties = {
  width: "32px",
  height: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--radius-sm, 6px)",
  background: "color-mix(in srgb, var(--text, #1a1714) 5%, transparent)",
  flexShrink: 0,
  fontSize: "18px",
  lineHeight: 1,
};

const emojiPlaceholderStyle: React.CSSProperties = {
  width: "32px",
  height: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--radius-sm, 6px)",
  background: "color-mix(in srgb, var(--text, #1a1714) 5%, transparent)",
  flexShrink: 0,
};

const textCellStyle: React.CSSProperties = {
  flex: "1 1 0",
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  overflow: "hidden",
};

const recipeTitleStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "14px",
  fontWeight: 500,
  color: "var(--text, #1a1714)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: 1.3,
};

const variationNameStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  color: "var(--muted, #71685b)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: 1.3,
};

const timestampStyle: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "11px",
  color: "var(--muted, #71685b)",
  fontVariantNumeric: "tabular-nums",
  flexShrink: 0,
  whiteSpace: "nowrap",
};

const emptyStateStyle: React.CSSProperties = {
  padding: "var(--space-6, 24px) 0",
  fontSize: "13px",
  color: "var(--muted, #71685b)",
  textAlign: "center",
  lineHeight: 1.6,
};

const errorStyle: React.CSSProperties = {
  padding: "var(--space-4, 16px) 0",
  fontSize: "13px",
  color: "var(--muted, #71685b)",
  textAlign: "center",
  lineHeight: 1.6,
};

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Format an epoch-ms timestamp as a concise relative label.
 * Uses Mono font via caller styles — this function returns only the string.
 */
function formatRelativeDate(epochMs: number): string {
  const now = Date.now();
  const diffMs = now - epochMs;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 2) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;

  const d = new Date(epochMs);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// CSS hover rule for draft rows — injected once per page.
const DRAFT_ROW_HOVER_CSS = `
  .proof-draft-row:hover {
    background: color-mix(in srgb, var(--text) 3%, transparent);
  }
`;

if (typeof document !== "undefined") {
  const id = "proof-draft-row-css";
  if (!document.getElementById(id)) {
    const el = document.createElement("style");
    el.id = id;
    el.textContent = DRAFT_ROW_HOVER_CSS;
    document.head.appendChild(el);
  }
}

// ── Load state ────────────────────────────────────────────────────────────────

type LoadState =
  { kind: "loading" } | { kind: "loaded"; drafts: DraftRow[] } | { kind: "error"; message: string };

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * DraftsDrawer — bottom sheet that lists all open drafts across the library.
 *
 * Uses the controlled Sheet API with snapPoints=["peek","full"] so on mobile
 * the user can swipe up to see the full list. No dismissGuard is needed —
 * the drawer is read-only.
 *
 * On desktop (≥768px) the Sheet renders as a centred modal per Sheet.tsx
 * desktop branch (design.md §2 — drag-to-snap is a touch interaction only).
 */
export function DraftsDrawer({
  open,
  onClose,
  recipes,
  apiClient,
  onNavigateToDraft,
}: DraftsDrawerProps) {
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  /**
   * Fetch open drafts across all recipes × variations.
   *
   * Strategy: for each variation in each recipe, call attempt.list with
   * status="draft". A draft status means the variation has an uncommitted
   * body. We collect the first-draft-per-variation (there is at most one
   * draft per variation at any time per the server invariant).
   *
   * Rule 8: any network failure surfaces as inline error copy.
   */
  const fetchDrafts = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const collected: DraftRow[] = [];
      let variationTotal = 0;
      let variationFailed = 0;

      for (const recipe of recipes) {
        const variations = recipe.variations ?? [];
        for (const variation of variations) {
          variationTotal += 1;
          let data: AttemptListResponse;
          try {
            data = (await apiClient.attempt.list({
              branchUuid: variation.uuid,
              status: "draft",
              limit: 1,
            })) as AttemptListResponse;
          } catch (innerErr) {
            // Per-variation fetch failure: log + accumulate failure count.
            // Rule 8: if every variation fails we surface an error state below.
            console.error(
              { event: "draft_list_variation_failed", variationUuid: variation.uuid },
              innerErr,
            );
            variationFailed += 1;
            continue;
          }
          const drafts = data?.attempts ?? [];
          const draft = drafts.find((a) => a.status === "draft");
          if (!draft) continue;
          collected.push({
            attemptUuid: draft.uuid,
            variationUuid: variation.uuid,
            recipeUuid: recipe.uuid,
            recipeTitle: recipe.title,
            // Recipe emoji is not part of RecipeListRow at this API version.
            // Defensive null — the SheetRow renders a placeholder square when null.
            recipeEmoji: null,
            variationName: variation.name,
            recipeVariationCount: variations.length,
            updatedAt: draft.updated_at ?? draft.created_at,
          });
        }
      }

      // If every variation call failed (all or at least one when there is only
      // one variation), surface an error state per Rule 8.
      if (variationTotal > 0 && variationFailed === variationTotal) {
        setState({
          kind: "error",
          message: "Could not load drafts. Check your connection and try again.",
        });
        return;
      }

      // Sort by most recently edited first.
      collected.sort((a, b) => b.updatedAt - a.updatedAt);
      setState({ kind: "loaded", drafts: collected });
    } catch (err) {
      // Rule 8 — surface the failure; never silently swallow.
      console.error({ event: "draft_list_failed", err });
      setState({
        kind: "error",
        message: "Could not load drafts. Check your connection and try again.",
      });
    }
  }, [apiClient, recipes]);

  // Fetch when the drawer opens.
  useEffect(() => {
    if (!open) return;
    void fetchDrafts();
  }, [open, fetchDrafts]);

  const draftsCount = state.kind === "loaded" ? state.drafts.length : null;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      snapPoints={["peek", "full"]}
      defaultSnapPoint="peek"
      aria-label="Drafts"
      data-testid="drafts-drawer"
    >
      <SheetHeader
        title={draftsCount !== null ? `Drafts (${draftsCount})` : "Drafts"}
        onDismiss={onClose}
      />
      <SheetBody data-testid="drafts-drawer-body">
        {state.kind === "loading" && (
          <div style={emptyStateStyle} data-testid="drafts-loading">
            Loading drafts…
          </div>
        )}

        {state.kind === "error" && (
          <div style={errorStyle} data-testid="drafts-error" role="alert">
            {state.message}
          </div>
        )}

        {state.kind === "loaded" && state.drafts.length === 0 && (
          <div style={emptyStateStyle} data-testid="drafts-empty">
            No open drafts.
          </div>
        )}

        {state.kind === "loaded" && state.drafts.length > 0 && (
          <ul style={listStyle} aria-label="Open drafts" data-testid="drafts-list">
            {state.drafts.map((draft) => (
              <li key={draft.attemptUuid} style={{ listStyle: "none" }}>
                {/*
                 * Each row is a button so it is keyboard-focusable and
                 * announces itself as interactive to assistive technology.
                 * Tap → navigate to that draft's editor.
                 */}
                <button
                  type="button"
                  className="proof-draft-row"
                  style={draftRowStyle(true)}
                  data-testid={`draft-row-${draft.attemptUuid}`}
                  aria-label={
                    isLegacyMultiVariation(draft.recipeVariationCount)
                      ? `Open draft for ${draft.recipeTitle} — ${draft.variationName}`
                      : `Open draft for ${draft.recipeTitle}`
                  }
                  onClick={() => {
                    onNavigateToDraft(draft.recipeUuid, draft.variationUuid);
                    onClose();
                  }}
                >
                  {/* Emoji cell */}
                  {draft.recipeEmoji ? (
                    <span style={emojiCellStyle} aria-hidden="true">
                      {draft.recipeEmoji}
                    </span>
                  ) : (
                    <span
                      style={emojiPlaceholderStyle}
                      aria-hidden="true"
                      data-testid={`draft-row-emoji-placeholder-${draft.attemptUuid}`}
                    />
                  )}

                  {/* Text cell — recipe title + (legacy only) variation name */}
                  <span style={textCellStyle}>
                    <span style={recipeTitleStyle}>{draft.recipeTitle}</span>
                    {isLegacyMultiVariation(draft.recipeVariationCount) && (
                      <span style={variationNameStyle}>{draft.variationName}</span>
                    )}
                  </span>

                  {/* Timestamp — Mono + tabular-nums per design.md §4 */}
                  <span
                    style={timestampStyle}
                    aria-label={`Last edited ${formatRelativeDate(draft.updatedAt)}`}
                  >
                    {formatRelativeDate(draft.updatedAt)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </SheetBody>
    </Sheet>
  );
}
